import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import { API_URLS } from "../../api/apiUrls";
// ─── Async Thunks ──────────────────────────────────────────────────────────

// Maps internal structure ID to the API enum value
// Ab hum component se seedha backend enum ("location-first"/"department-first") bhej rahe hain
export const fetchProfileData = createAsyncThunk(
  "group/fetchProfileData",
  async (id, { rejectWithValue }) => {
    try {
      const url = API_URLS.LICENSEACCESS(id);
      const response = await axiosInstance.get(url);
      return response.data.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch profile data";
      return rejectWithValue(message);
    }
  },
);
export const createSubAdmin = createAsyncThunk(
  "group/createSubAdmin",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        API_URLS.CREATE_SUB_ADMIN,
        payload,
      );
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create sub-admin";
      return rejectWithValue(message);
    }
  },
);

export const updateProfileData = createAsyncThunk(
  "profile/updateProfileData",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        API_URLS.UPDATE_CLIENT_PROFILE,
        formData,
      );
      if (response.data?.success === false) {
        return rejectWithValue(
          response.data?.message || "Failed to update profile",
        );
      }
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update profile";
      return rejectWithValue(message);
    }
  },
);
export const updateOrganizationData = createAsyncThunk(
  "profile/updateOrganizationData",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        API_URLS.UPDATE_ORGANIZATION_CLIENT_PROFILE,
        formData,
      );
      if (response.data?.success === false) {
        return rejectWithValue(
          response.data?.message || "Failed to update profile",
        );
      }
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update profile";
      return rejectWithValue(message);
    }
  },
);

export const sendBillingOtp = createAsyncThunk(
  "profile/sendBillingOtp",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        API_URLS.CLIENT_SEND_BILLING_OTP,
        { email },
      );
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send OTP";
      return rejectWithValue(message);
    }
  },
);

export const verifyBillingOtp = createAsyncThunk(
  "profile/verifyBillingOtp",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        API_URLS.CLIENT_VERIFY_BILLING_OTP,
        { email, otp },
      );
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to verify OTP";
      return rejectWithValue(message);
    }
  },
);

// ─── Initial State ─────────────────────────────────────────────────────────

const initialState = {
  currentPage: 1,
  pageSize: 5,
  search: "",
  loading: false,
  error: null,

  profileData: [],
  profileUpdateLoading: false,
  profileUpdateError: null,
  billingOtpLoading: false,
  billingOtpSent: false,
  billingOtpVerified: false,
  billingOtpError: null,
  createSubAdminData: null,
  createSubAdminLoading: false,
  createSubAdminSuccess: false,
  createSubAdminError: null,
};

const updateSubAdminLicenseCount = (profileData, licenseCount) => {
  const profile = Array.isArray(profileData) ? profileData[0] : profileData;

  if (!profile?.license?.sub_admin) {
    return;
  }

  profile.license.sub_admin.used =
    (profile.license.sub_admin.used || 0) + licenseCount;
  profile.license.sub_admin.unused =
    (profile.license.sub_admin.unused || 0) - licenseCount;
};

const patchProfileBillingEmailPending = (profileData, pendingEmail) => {
  const profile = Array.isArray(profileData) ? profileData[0] : profileData;
  if (!profile) return;

  profile.billing = profile.billing || {};
  profile.billing.billingContact = profile.billing.billingContact || {};
  profile.billing.billingContact.emailVerification = {
    ...profile.billing.billingContact.emailVerification,
    isVerified: false,
    pendingEmail: String(pendingEmail).trim().toLowerCase(),
    otp: null,
  };
};

// ─── Slice ────────────────────────────────────────────────────────────────

const profileSlice = createSlice({
  name: "organizationSetup",
  initialState,
  reducers: {
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    setPageSize(state, action) {
      state.pageSize = action.payload;
    },
    setSearch(state, action) {
      state.search = action.payload;
      state.currentPage = 1;
    },
    clearSetupError(state) {
      state.error = null;
      state.createSubAdminError = null;
    },
    clearCreateSubAdminStatus(state) {
      state.createSubAdminSuccess = false;
      state.createSubAdminError = null;
      state.createSubAdminData = null;
    },
    resetBillingOtpState(state) {
      state.billingOtpSent = false;
      state.billingOtpVerified = false;
      state.billingOtpError = null;
      const profile = Array.isArray(state.profileData)
        ? state.profileData[0]
        : state.profileData;
      const verification = profile?.billing?.billingContact?.emailVerification;
      if (verification) {
        verification.pendingEmail = null;
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // Set Org Mode
      .addCase(fetchProfileData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfileData.fulfilled, (state, action) => {
        state.loading = false;
        state.profileData = action.payload ?? [];
      })
      .addCase(fetchProfileData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.profileData = [];
      })
      .addCase(updateProfileData.pending, (state) => {
        state.profileUpdateLoading = true;
        state.profileUpdateError = null;
      })
      .addCase(updateProfileData.fulfilled, (state) => {
        state.profileUpdateLoading = false;
        state.profileUpdateError = null;
      })
      .addCase(updateProfileData.rejected, (state, action) => {
        state.profileUpdateLoading = false;
        state.profileUpdateError = action.payload;
      })
      .addCase(updateOrganizationData.pending, (state) => {
        state.profileUpdateLoading = true;
        state.profileUpdateError = null;
      })
      .addCase(updateOrganizationData.fulfilled, (state) => {
        state.profileUpdateLoading = false;
        state.profileUpdateError = null;
      })
      .addCase(updateOrganizationData.rejected, (state, action) => {
        state.profileUpdateLoading = false;
        state.profileUpdateError = action.payload;
      })
      .addCase(createSubAdmin.pending, (state) => {
        state.createSubAdminLoading = true;
        state.createSubAdminSuccess = false;
        state.createSubAdminError = null;
      })
      .addCase(createSubAdmin.fulfilled, (state, action) => {
        state.createSubAdminLoading = false;
        state.createSubAdminSuccess = action.payload?.success !== false;
        state.createSubAdminData = action.payload?.data ?? action.payload;
        state.createSubAdminError =
          action.payload?.success === false
            ? action.payload?.message || "Failed to create sub-admin"
            : null;

        if (action.payload?.success !== false) {
          const projectIds =
            action.payload?.data?.empRole?.scope?.projectIds || [];
          const licenseCount = projectIds.length <= 1 ? 1 : projectIds.length;

          updateSubAdminLicenseCount(state.profileData, licenseCount);
        }
      })
      .addCase(createSubAdmin.rejected, (state, action) => {
        state.createSubAdminLoading = false;
        state.createSubAdminSuccess = false;
        state.createSubAdminError = action.payload;
        state.createSubAdminData = null;
      })
      .addCase(sendBillingOtp.pending, (state) => {
        state.billingOtpLoading = true;
        state.billingOtpError = null;
      })
      .addCase(sendBillingOtp.fulfilled, (state, action) => {
        state.billingOtpLoading = false;
        state.billingOtpSent = true;
        const pendingEmail = action.meta?.arg?.email;
        if (pendingEmail) {
          patchProfileBillingEmailPending(state.profileData, pendingEmail);
        }
      })
      .addCase(sendBillingOtp.rejected, (state, action) => {
        state.billingOtpLoading = false;
        state.billingOtpError = action.payload;
      })
      .addCase(verifyBillingOtp.pending, (state) => {
        state.billingOtpLoading = true;
        state.billingOtpError = null;
      })
      .addCase(verifyBillingOtp.fulfilled, (state) => {
        state.billingOtpLoading = false;
        state.billingOtpVerified = true;
      })
      .addCase(verifyBillingOtp.rejected, (state, action) => {
        state.billingOtpLoading = false;
        state.billingOtpError = action.payload;
      });
  },
});

export const {
  setCurrentPage,
  setPageSize,
  setSearch,
  clearSetupError,
  clearCreateSubAdminStatus,
  resetBillingOtpState,
} = profileSlice.actions;

export default profileSlice.reducer;
